import { useEffect } from "react";

/**
 * Measures the height of `elementRef.current` and writes it as a CSS custom
 * property on the target element.
 *
 * - Defaults to `document.documentElement` when no `targetRef` is given,
 *   which matches the Header.js use-case.
 * - Pass a `targetRef` to scope the variable to a specific container element,
 *   which matches the ReportTableView.js use-case.
 * - Pass extra values in `deps` to re-trigger the observation when a
 *   conditionally-mounted element is toggled (e.g. `[showLoader]`).
 *
 * @param {React.RefObject} elementRef   - ref of the element to measure
 * @param {string}          variableName - CSS custom property name, e.g. "--sticky-page-header-height"
 * @param {React.RefObject} [targetRef]  - ref of the element to set the property on;
 *                                         defaults to document.documentElement
 * @param {Array}           [deps=[]]    - extra dependencies that trigger re-observation
 */
export default function useStickyHeight(
    elementRef,
    variableName,
    targetRef = null,
    deps = []
) {
    useEffect(() => {
        const element = elementRef.current;
        // Capture target early so the cleanup closure always holds a valid reference,
        // even if targetRef.current has been set to null by the time cleanup runs.
        const target = targetRef?.current ?? document.documentElement;

        if (!element) {
            return undefined;
        }

        const setHeight = () => {
            const height = element.getBoundingClientRect().height;
            target.style.setProperty(variableName, `${height}px`);
        };

        setHeight();

        if (typeof ResizeObserver !== "undefined") {
            const observer = new ResizeObserver(setHeight);
            observer.observe(element);

            return () => {
                observer.disconnect();
                target.style.removeProperty(variableName);
            };
        }

        // Fallback for environments without ResizeObserver
        window.addEventListener("resize", setHeight);

        return () => {
            window.removeEventListener("resize", setHeight);
            target.style.removeProperty(variableName);
        };
        // deps spread is intentional: callers supply the extra values that
        // should retrigger the effect (e.g. [showLoader]).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [elementRef, variableName, targetRef, ...deps]);
}
